import React, { useState } from 'react';
import { Building, X } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';

const OciUserForm = ({ userData, onSave, onCancel, isEditing = false }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [formData, setFormData] = useState(userData || {
    username: '',
    password: '',
    email: '',
    company: '',
    is_active: true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="relative group">
      {/* Form Background */}
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20 backdrop-blur-2xl'
        : 'glass-light-card border border-slate-200'
      } rounded-3xl ${isDarkMode ? 'shadow-[0_8px_32px_rgba(0,0,0,0.4)]' : 'shadow-sm'}`}></div>
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
        : ''
      } rounded-3xl`}></div>
      
      
      <div className="relative p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            {/* Icon */}
            <div className="relative group/icon">
              <div className={`${isDarkMode ? '' : 'hidden'} absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-2xl blur-sm`}></div>
              <div className={`relative w-10 h-10 rounded-2xl flex items-center justify-center border ${isDarkMode ? 'bg-gradient-to-br from-purple-600/60 via-purple-700/50 to-purple-800/60 backdrop-blur-xl shadow-[0_8px_32px_rgba(147,51,234,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border-white/20' : 'bg-brand/10 border-brand/20'}`}>
                <Building className={`w-5 h-5 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-brand'}`} />
                <div className={`${isDarkMode ? '' : 'hidden'} absolute top-1 left-1 w-2.5 h-2.5 bg-white/30 rounded-full blur-sm`}></div>
              </div>
            </div>
            <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'}`}>
              {isEditing ? t('admin.ociForm.editTitle') : t('admin.ociForm.createTitle')}
            </h3>
          </div>
          
          {/* Close Button */}
          <button
            onClick={onCancel}
            className={`relative group/close overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${isDarkMode ? '' : 'bg-slate-100 hover:bg-slate-200 border border-slate-200'}`}
            style={isDarkMode ? {
              background: 'linear-gradient(135deg, rgba(71,85,105,0.6) 0%, rgba(75,85,99,0.6) 50%, rgba(71,85,105,0.6) 100%)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.3)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
            } : {}}
          >
            <div
              className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/close:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
              style={isDarkMode ? {
                background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)'
              } : {}}
            ></div>

            <div className={`relative z-10 p-2 flex items-center justify-center transition-all duration-300 ${isDarkMode ? 'text-white drop-shadow-lg group-hover/close:drop-shadow-xl' : 'text-slate-700'}`}>
              <X className="w-4 h-4 transition-transform duration-300 group-hover/close:scale-110" />
            </div>

            <div className={`absolute inset-0 opacity-0 group-hover/close:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
              <div
                className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/close:translate-x-full transition-transform duration-800"
                style={isDarkMode ? {
                  background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)'
                } : {}}
              ></div>
            </div>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>
                {t('admin.ociForm.username')}
              </label>
              <div className="relative">
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                  : 'bg-white border border-slate-200'
                } rounded-2xl`}></div>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                    ? 'text-white placeholder-white/50 focus:ring-2 focus:ring-purple-400/50'
                    : 'text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'
                  }`}
                  required
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>
                {t('admin.ociForm.password')}
              </label>
              <div className="relative">
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                  : 'bg-white border border-slate-200'
                } rounded-2xl`}></div>
                <input
                  type="text"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                    ? 'text-white placeholder-white/50 focus:ring-2 focus:ring-purple-400/50'
                    : 'text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'
                  }`}
                  required
                  placeholder={t('admin.ociForm.passwordHint')}
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>
                {t('admin.ociForm.email')}
              </label>
              <div className="relative">
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                  : 'bg-white border border-slate-200'
                } rounded-2xl`}></div>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                    ? 'text-white placeholder-white/50 focus:ring-2 focus:ring-purple-400/50'
                    : 'text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'
                  }`}
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>
                {t('admin.ociForm.company')}
              </label>
              <div className="relative">
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                  : 'bg-white border border-slate-200'
                } rounded-2xl`}></div>
                <input
                  type="text"
                  value={formData.company || ''}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                    ? 'text-white placeholder-white/50 focus:ring-2 focus:ring-purple-400/50'
                    : 'text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'
                  }`}
                />
              </div>
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              className="mr-2 rounded border-white/30 bg-transparent text-purple-500 focus:ring-purple-400/50 focus:ring-offset-0"
            />
            <label htmlFor="is_active" className={`block text-sm ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'}`}>
              {t('admin.ociForm.isActive')}
            </label>
          </div>

          <div className="flex justify-end space-x-3">
            {/* Cancel Button */}
            <button
              type="button"
              onClick={onCancel}
              className={`relative group/cancel overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${isDarkMode ? '' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200'}`}
              style={isDarkMode ? {
                background: 'linear-gradient(135deg, rgba(71,85,105,0.6) 0%, rgba(75,85,99,0.6) 50%, rgba(71,85,105,0.6) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
              } : {}}
            >
              <div
                className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/cancel:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                style={isDarkMode ? {
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)'
                } : {}}
              ></div>

              <div className={`relative z-10 px-4 py-2 flex items-center justify-center font-medium transition-all duration-300 ${isDarkMode ? 'text-white drop-shadow-lg group-hover/cancel:drop-shadow-xl' : ''}`}>
                {t('common.cancel')}
              </div>

              <div className={`absolute inset-0 opacity-0 group-hover/cancel:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                <div
                  className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/cancel:translate-x-full transition-transform duration-800"
                  style={isDarkMode ? {
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)'
                  } : {}}
                ></div>
              </div>
            </button>

            {/* Submit Button */}
            <button
              type="submit"
              className={`relative group/submit overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] ${isDarkMode ? '' : 'bg-brand hover:bg-[#1D4ED8] text-white shadow-sm'}`}
              style={isDarkMode ? {
                background: 'linear-gradient(135deg, rgba(147,51,234,0.7) 0%, rgba(126,34,206,0.7) 50%, rgba(107,33,168,0.7) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow: '0 8px 32px rgba(147,51,234,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
              } : {}}
            >
              <div
                className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/submit:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                style={isDarkMode ? {
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                } : {}}
              ></div>

              <div className={`relative z-10 px-4 py-2 flex items-center justify-center font-medium text-white transition-all duration-300 ${isDarkMode ? 'drop-shadow-lg group-hover/submit:drop-shadow-xl' : ''}`}>
                {isEditing ? t('common.update') : t('common.create')}
              </div>

              <div className={`absolute inset-0 opacity-0 group-hover/submit:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                <div
                  className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/submit:translate-x-full transition-transform duration-800"
                  style={isDarkMode ? {
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                  } : {}}
                ></div>
              </div>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OciUserForm;